import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { firmAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface FirmDisposition {
  id: number;
  name: string;
  is_active: boolean;
  firm_id: number | null;
}

const toDatetimeLocal = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const ClientFirmSettings: React.FC = () => {
  const { firmId } = useParams<{ firmId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState({
    category: '' as 'prepaid' | 'postpaid' | 'trial' | 'suspended' | '',
    currency: 'USD',
    access_mode: 'full' as 'full' | 'ceased' | 'download_only',
    download_access_until: '',
    snooze_client_estimate_emails: false,
  });
  const [effectiveAccessMode, setEffectiveAccessMode] = useState<'full' | 'ceased' | 'download_only'>('full');
  const [dispositions, setDispositions] = useState<FirmDisposition[]>([]);
  const [dispositionsLoading, setDispositionsLoading] = useState(false);
  const [dispositionDialogOpen, setDispositionDialogOpen] = useState(false);
  const [dispositionEditId, setDispositionEditId] = useState<number | null>(null);
  const [dispositionName, setDispositionName] = useState('');
  const [dispositionIsActive, setDispositionIsActive] = useState(true);
  const [dispositionSaving, setDispositionSaving] = useState(false);

  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin');
  };

  const isSiteAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'site_admin');
  };

  const isPmpAdminOrSiteAdmin = () => {
    return isPmpAdmin() || isSiteAdmin();
  };

  const currencies = ['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD', 'JPY', 'CNY'];

  useEffect(() => {
    if (!isPmpAdminOrSiteAdmin()) {
      setError('Only PMP admins or Site admins can access firm settings');
      return;
    }
    fetchSettings();
    fetchDispositions();
  }, [firmId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await firmAPI.getSettings(parseInt(firmId!));
      if (response.data.success) {
        setSettings({
          category: response.data.data.category || '',
          currency: response.data.data.currency || 'USD',
          access_mode: response.data.data.access_mode || 'full',
          download_access_until: toDatetimeLocal(response.data.data.download_access_until),
          snooze_client_estimate_emails: !!response.data.data.snooze_client_estimate_emails,
        });
        setEffectiveAccessMode(response.data.data.effective_access_mode || response.data.data.access_mode || 'full');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchDispositions = async () => {
    try {
      setDispositionsLoading(true);
      const response = await firmAPI.getDispositions(parseInt(firmId!));
      if (response.data.success) {
        setDispositions(response.data.data || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch dispositions');
    } finally {
      setDispositionsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (settings.access_mode === 'download_only' && !settings.download_access_until) {
        setError('Choose the date and time download access should end.');
        return;
      }

      setSaving(true);
      setError('');
      setSuccess('');

      const response = await firmAPI.updateSettings(parseInt(firmId!), {
        category: settings.category,
        currency: settings.currency,
        access_mode: settings.access_mode,
        download_access_until: settings.access_mode === 'download_only' && settings.download_access_until
          ? new Date(settings.download_access_until).toISOString()
          : null,
        snooze_client_estimate_emails: settings.snooze_client_estimate_emails,
      });
      if (response.data.success) {
        setEffectiveAccessMode(response.data.data.effective_access_mode || settings.access_mode);
        setSuccess('Settings updated successfully');
      }
    } catch (err: any) {
      const fieldErrors = err.response?.data?.errors;
      const firstFieldError = fieldErrors
        ? (Object.values(fieldErrors).flat()[0] as string | undefined)
        : undefined;
      setError(firstFieldError || err.response?.data?.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const openAddDisposition = () => {
    setDispositionEditId(null);
    setDispositionName('');
    setDispositionIsActive(true);
    setDispositionDialogOpen(true);
  };

  const openEditDisposition = (d: FirmDisposition) => {
    setDispositionEditId(d.id);
    setDispositionName(d.name);
    setDispositionIsActive(d.is_active);
    setDispositionDialogOpen(true);
  };

  const handleSaveDisposition = async () => {
    const name = dispositionName.trim();
    if (!name) {
      setError('Disposition name is required');
      return;
    }
    try {
      setDispositionSaving(true);
      setError('');
      if (dispositionEditId != null) {
        await firmAPI.updateDisposition(parseInt(firmId!), dispositionEditId, {
          name,
          is_active: dispositionIsActive,
        });
        setSuccess('Disposition updated');
      } else {
        await firmAPI.createDisposition(parseInt(firmId!), {
          name,
          is_active: dispositionIsActive,
        });
        setSuccess('Disposition added');
      }
      setDispositionDialogOpen(false);
      await fetchDispositions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save disposition');
    } finally {
      setDispositionSaving(false);
    }
  };

  const handleDeleteDisposition = async (id: number) => {
    if (!window.confirm('Deactivate this disposition? It will no longer appear in timer dropdowns for this client.')) {
      return;
    }
    try {
      setError('');
      await firmAPI.deleteDisposition(parseInt(firmId!), id);
      setSuccess('Disposition deactivated');
      await fetchDispositions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to deactivate disposition');
    }
  };

  if (!isPmpAdminOrSiteAdmin()) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">Only PMP admins or Site admins can access firm settings</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" href="#" onClick={() => navigate('/firms')}>
          Firms
        </Link>
        <Link color="inherit" href="#" onClick={() => navigate(`/firms/${firmId}`)}>
          Firm Details
        </Link>
        <Typography color="text.primary">Settings</Typography>
      </Breadcrumbs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 4, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Client Firm Settings
        </Typography>

        <Box sx={{ mt: 3 }}>
          <FormControl fullWidth sx={{ mb: 3 }} disabled={!isPmpAdmin()}>
            <InputLabel>Category</InputLabel>
            <Select
              value={settings.category}
              label="Category"
              onChange={(e) => setSettings({ ...settings, category: e.target.value as 'prepaid' | 'postpaid' | 'trial' | 'suspended' })}
            >
              <MenuItem value="prepaid">Prepaid</MenuItem>
              <MenuItem value="postpaid">Postpaid</MenuItem>
              <MenuItem value="trial">Trial</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 3 }} disabled={!isPmpAdmin()}>
            <InputLabel>Currency</InputLabel>
            <Select
              value={settings.currency}
              label="Currency"
              onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
            >
              {currencies.map((curr) => (
                <MenuItem key={curr} value={curr}>
                  {curr}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>
            Estimate emails
          </Typography>
          <FormControlLabel
            sx={{ mb: 0.5 }}
            control={
              <Switch
                checked={settings.snooze_client_estimate_emails}
                disabled={!isPmpAdmin()}
                onChange={(e) => setSettings({ ...settings, snooze_client_estimate_emails: e.target.checked })}
              />
            }
            label="Snooze estimate approval emails to client users"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            When on, submitting a client estimate for approval does not email this firm&apos;s client admins, managers, or finance users. The estimate still waits for approval in the app, and PMP admins are still emailed.
          </Typography>

          <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>
            Emergency access
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Put sign-in on hold immediately. People at this firm cannot log in until you restore access. The sign-in page asks them to reach out to sohail@appelsolutions.com or PMP@appelsolutions.com. You can also allow download-only access until a date and time you choose.
          </Typography>

          {settings.access_mode === 'download_only' && effectiveAccessMode === 'ceased' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This download window has ended. Users cannot sign in until you restore full access or set a new end time.
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 3 }} disabled={!isPmpAdmin()}>
            <InputLabel>Sign-in access</InputLabel>
            <Select
              value={settings.access_mode}
              label="Sign-in access"
              onChange={(e) => setSettings({
                ...settings,
                access_mode: e.target.value as 'full' | 'ceased' | 'download_only',
              })}
            >
              <MenuItem value="full">Full access</MenuItem>
              <MenuItem value="ceased">Suspend sign-in</MenuItem>
              <MenuItem value="download_only">Download only (limited time)</MenuItem>
            </Select>
          </FormControl>

          {settings.access_mode === 'download_only' && (
            <TextField
              fullWidth
              sx={{ mb: 3 }}
              disabled={!isPmpAdmin()}
              label="Download access until"
              type="datetime-local"
              value={settings.download_access_until}
              onChange={(e) => setSettings({ ...settings, download_access_until: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Until this time, users can sign in and download case files only. After it passes, sign-in is blocked again."
            />
          )}

          {isPmpAdmin() && (
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/firms/${firmId}`)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? <CircularProgress size={24} /> : 'Save Settings'}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6">Client-specific dispositions</Typography>
            <Typography variant="body2" color="text.secondary">
              These extra dispositions appear only on this client&apos;s cases, in addition to the global list from Timer Configuration.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={openAddDisposition}>
            Add disposition
          </Button>
        </Box>

        {dispositionsLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dispositions.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>{d.id}</TableCell>
                    <TableCell>{d.name}</TableCell>
                    <TableCell>{d.is_active ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEditDisposition(d)} aria-label="Edit">
                        <Edit />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteDisposition(d.id)} aria-label="Deactivate" color="error">
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {dispositions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No client-specific dispositions. Add one to make it available only for this firm.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={dispositionDialogOpen} onClose={() => setDispositionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{dispositionEditId != null ? 'Edit Disposition' : 'Add Disposition'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={dispositionName}
            onChange={(e) => setDispositionName(e.target.value)}
          />
          <FormControlLabel
            control={<Switch checked={dispositionIsActive} onChange={(e) => setDispositionIsActive(e.target.checked)} />}
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDispositionDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveDisposition} variant="contained" disabled={dispositionSaving}>
            {dispositionSaving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ClientFirmSettings;
